import { useEffect, useState } from 'react'

/**
 * Installing TapWork as an app (Chrome/Edge "Install TapWork").
 *
 * The browser fires `beforeinstallprompt` once it considers the site installable (manifest +
 * service worker + HTTPS). We keep that event so the app can offer its own "Install" control
 * rather than relying on the small icon in the address bar.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

let deferred: BeforeInstallPromptEvent | null = null
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((fn) => fn())

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferred = event as BeforeInstallPromptEvent
    notify()
  })
  window.addEventListener('appinstalled', () => {
    deferred = null
    notify()
  })
}

export function registerServiceWorker(): void {
  // Production only: in dev a worker would sit between Vite's HMR and the page.
  if (!import.meta.env.PROD || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => {
      // Install is a nicety; the app works without the worker.
    })
  })
}

export function isRunningAsApp(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

/** `canInstall` is true when the browser will show its install prompt; `install()` shows it. */
export function useInstallApp(): { canInstall: boolean; installed: boolean; install: () => Promise<boolean> } {
  const [, force] = useState(0)
  useEffect(() => {
    const fn = () => force((n) => n + 1)
    listeners.add(fn)
    return () => {
      listeners.delete(fn)
    }
  }, [])
  return {
    canInstall: deferred !== null,
    installed: isRunningAsApp(),
    install: async () => {
      const event = deferred
      if (!event) return false
      await event.prompt()
      const choice = await event.userChoice
      deferred = null
      notify()
      return choice.outcome === 'accepted'
    },
  }
}
